import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Modal, Form } from 'react-bootstrap';
import '../../assets/css/WhatsAppSetup.css';
import UserSessionManager from '../../utils/UserSessionManager';
import WhatsAppServices from '../../services/WhatsAppServices';
import WhatsAppConfiguration from './WhatsAppConfiguration';
import WhatsAppOAuth from './WhatsAppOAuth';
import NurturixCustomTabs from '../../components/common/NurturixCustomTabs';
import { FaSignOutAlt, FaUser, FaTrash } from 'react-icons/fa';

import {
  WhatsAppConnectionStatus,
  WhatsAppOAuthStatus,
  WhatsAppTexts,
  WhatsAppTabIds,
  WhatsAppLoadingStates,
  WhatsAppSetupSteps,
  ServerConnectionStatus
} from '../../constants/whatsAppConstants';
import WhatsAppChat from './WhatsAppChat';
import { API_STATUS } from '../../constants/apiStatusCode';
import WhatsAppTemplate from '../WhatsAppTemplate/WhatsAppTemplate';
import Broadcast from '../Broadcast/Broadcast';
import Group from '../Group/Group';


const WhatsApp = () => {
  // State management
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [serverStatus, setServerStatus] = useState(null);
  const [whatsAppConnectionStatus, setConnectionStatus] = useState(WhatsAppConnectionStatus(ServerConnectionStatus.CHECKING));
  const [whatsAppOAuthStatus, setWhatsAppOAuthStatus] = useState(WhatsAppOAuthStatus.NOT_CONNECTED);
  const [webhookGenerated, setWebhookGenerated] = useState(false);
  const [webhookCredentials, setWebhookCredentials] = useState(null);
  const [loadingStates, setLoadingStates] = useState({
    connecting: false,
    generating: false,
    testing: false,
    savingConfiguration: false
  });
  const [configurationError, setConfigurationError] = useState('');
  
  // Sign in/Sign out state
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Check if user is logged in (check localStorage or session)
    return !!localStorage.getItem('whatsappLoggedIn') || false;
  });
  const [showSignInModal, setShowSignInModal] = useState(!isLoggedIn);
  const [wabaId, setWabaId] = useState('');
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState('');

  const userID = UserSessionManager.getSession()?.businessAdminId;

  // Tab configuration
  const tabs = [
    {
      key: WhatsAppTabIds.WHATSAPP,
      label: WhatsAppTexts.WHATSAPP_DASHBOARD_TAB_TITLE,
      //icon: <FaWhatsapp />
    },
    {
      key: WhatsAppTabIds.TEMPLATES,
      label: WhatsAppTexts.TEMPLATES_TAB_TITLE,
    },
    {
      key: WhatsAppTabIds.GROUP,
      label: WhatsAppTexts.GROUPS_TAB_TITLE,
    },
    {
      key: WhatsAppTabIds.BROADCAST,
      label: WhatsAppTexts.BROADCAST_TAB_TITLE,
    }
  ];

  // Check connection status on component mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);


  const checkConnectionStatus = async () => {
    try {
      setIsLoggedIn(localStorage.getItem('whatsappLoggedIn'));
      const response = await WhatsAppServices.getWhatsAppConnectionStatus();
      const serverStatus = response.data;
      const whatsAppConnectionStatus = WhatsAppConnectionStatus(serverStatus.status);
      console.log('whatsAppConnectionStatus', whatsAppConnectionStatus);
      setConnectionStatus(whatsAppConnectionStatus);
      setServerStatus(serverStatus);
    } catch (error) {
      console.error('Error checking connection status:', error);
      setConnectionStatus(WhatsAppConnectionStatus('NOT_CONNECTED'));
      setServerStatus(null);
    }
  };

  const connectWhatsAppForOAuth = async () => {
    setLoadingStates(prev => ({ ...prev, connecting: true }));

    try {
      if (!serverStatus.connectUrl) {
        console.error('WhatsApp connection URL not found');
        setLoadingStates(prev => ({ ...prev, connecting: false }));
        return;
      }

      const windowPopup = window.open(
        serverStatus.connectUrl,
        "_blank",
        "width=600,height=700,top=100,left=100"
      );


      // Message handler - must be on the opener window (window), not the popup
      const handleMessage = async (event) => {
        console.log('WhatsApp addEventListener', event.origin, 'Expected:', window.location.origin);
        console.log('Event data:', event.data);

        // Check origin - must match current window origin
        if (event.origin !== window.location.origin) {
          console.warn('Origin mismatch:', event.origin, 'vs', window.location.origin);
          return;
        }

        const { success } = event.data;
        if (success) {
          console.log('WhatsApp connected successfully');
          setWhatsAppOAuthStatus(WhatsAppOAuthStatus.CONNECTED);
          setConnectionStatus(WhatsAppConnectionStatus(ServerConnectionStatus.PENDING));
          setLoadingStates(prev => ({ ...prev, connecting: false }));
          windowPopup.close();
          // Remove the event listener after successful connection
          window.removeEventListener('message', handleMessage);
        } else {
          console.error('WhatsApp connection failed');
          setLoadingStates(prev => ({ ...prev, connecting: false }));
          windowPopup.close();
          window.removeEventListener('message', handleMessage);
          setWhatsAppOAuthStatus(WhatsAppOAuthStatus.NOT_CONNECTED);
        }
      };

      // Add event listener to the opener window (current window)
      window.addEventListener('message', handleMessage);

      // Also check if popup is closed manually or facebook does that automatically
      const checkPopupClosed = setInterval(async () => {
        if (windowPopup.closed) {
          clearInterval(checkPopupClosed);
          window.removeEventListener('message', handleMessage);
          setLoadingStates(prev => ({ ...prev, connecting: false }));
          setWhatsAppOAuthStatus(WhatsAppOAuthStatus.CONNECTED);

          // update the status so we can reload the property
          setConnectionStatus(WhatsAppConnectionStatus(ServerConnectionStatus.PENDING));

        }
      }, 1000);
    } catch (error) {
      console.error('Error connecting WhatsApp:', error);
      setLoadingStates(prev => ({ ...prev, connecting: false }));
    }
  };


  const generateWebhook = async () => {
    setLoadingStates(prev => ({ ...prev, generating: true }));

    try {

      const response = await WhatsAppServices.getWebhookConfiguration();
      if (response.status === 200) {
        setWebhookGenerated(true);
        setWebhookCredentials(response.data);
        console.log('webhookCredentials', webhookCredentials);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error generating webhook:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, generating: false }));
    }
  };

  const confirmWebhookUpdated = async () => {
    localStorage.setItem('whatsappLoggedIn', 'true');
    await checkConnectionStatus();
  }

  // Handle configuration save
  const handleSaveConfiguration = async (configuration) => {
    setLoadingStates(prev => ({ ...prev, savingConfiguration: true }));
    setConfigurationError('');

    try {
      const response = await WhatsAppServices.saveWhatsAppConfiguration(configuration);

      if (response.status === 200) {
        // Configuration saved successfully, check connection status again
        await checkConnectionStatus();
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      if (error.status === API_STATUS.CONFLICT) {
        try {
          let status = await checkConnectionStatus();
          console.log('status', status);
        }
        catch (statusError) {
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to save configuration. Please try again.';
          setConfigurationError(errorMessage);
        }
      }
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to save configuration. Please try again.';
      setConfigurationError(errorMessage);
    } finally {
      setLoadingStates(prev => ({ ...prev, savingConfiguration: false }));
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  // Handle sign in with WABA ID
  const handleSignIn = async () => {
    if (!wabaId.trim()) {
      setSignInError('Please enter WABA ID');
      return;
    }

    try {
      setSignInLoading(true);
      setSignInError('');
      
      // Call login API with WABA ID
      const response = await WhatsAppServices.login(wabaId.trim());
      
      // If login is successful, store login state and close modal
      if (response?.data?.success || response?.status === 200 || response?.data?.statusCode === API_STATUS.SUCCESS) {
        localStorage.setItem('whatsappLoggedIn', 'true');
        localStorage.setItem('wabaId', wabaId.trim());
        setIsLoggedIn(true);
        setShowSignInModal(false);
        setWabaId('');
        // Refresh connection status after login
        await checkConnectionStatus();
      } else {
        setSignInError(response?.data?.message || 'Failed to sign in. Please try again.');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      setSignInError(error?.response?.data?.message || 'Failed to sign in. Please check your WABA ID and try again.');
    } finally {
      setSignInLoading(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      const storedWabaId = localStorage.getItem('wabaId');
      if (storedWabaId) {
        await WhatsAppServices.logout(storedWabaId);
      }
      localStorage.removeItem('whatsappLoggedIn');
      localStorage.removeItem('wabaId');
      setIsLoggedIn(false);
      setShowSignInModal(true);
      setActiveTab('whatsapp');
    } catch (error) {
      console.error('Sign out error:', error);
      // Still sign out locally even if API fails
      localStorage.removeItem('whatsappLoggedIn');
      localStorage.removeItem('wabaId');
      setIsLoggedIn(false);
      setShowSignInModal(true);
    }
  };

  // Handle erase all data
  const handleEraseData = async () => {
    if (!window.confirm('Are you sure you want to erase all data? This action cannot be undone.')) {
      return;
    }

    try {
      await WhatsAppServices.eraseData();
      alert('All data has been erased successfully');
      // Close the sign-in modal
      setShowSignInModal(false);  
      // Set connection status to NEEDS_CONFIGURATION to show WhatsAppConfiguration page
      setConnectionStatus(WhatsAppConnectionStatus("INCOMPLETE_CONFIGURATION"));
    } catch (error) {
      alert(error?.response?.data?.message || 'Failed to erase data. Please try again.');
    }
  };

  // Show sign in modal if not logged in
  if (!isLoggedIn && whatsAppConnectionStatus === ServerConnectionStatus.CONNECTED) {
    debugger
    console.log("connection status",whatsAppConnectionStatus)
    return (
      <>
        {/* Sign In Modal */}
        <Modal
          show={showSignInModal}
          onHide={() => {}} // Prevent closing by clicking outside
          centered
          backdrop="static"
        >
          <Modal.Body className="text-center p-5">
            {/* User Avatar */}
            <div className="mb-4">
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#007bff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  overflow: 'hidden'
                }}
              >
                <FaUser size={40} color="white" />
              </div>
            </div>

            {/* Welcome Message */}
            <h4 className="fw-bold mb-2">Welcome Back</h4>
            <p className="text-muted mb-4">
              Please sign in to access your WhatsApp Business dashboard
            </p>

            {/* WABA ID Input */}
            <Form.Group className="mb-4">
              <Form.Control
                type="text"
                placeholder="Enter WABA ID"
                value={wabaId}
                onChange={(e) => {
                  setWabaId(e.target.value);
                  setSignInError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSignIn();
                  }
                }}
                disabled={signInLoading}
              />
              {signInError && (
                <Form.Text className="text-danger d-block mt-2">
                  {signInError}
                </Form.Text>
              )}
            </Form.Group>

            {/* Sign In Button */}
            <Button
              variant="primary"
              className="w-100 mb-3"
              onClick={handleSignIn}
              disabled={signInLoading || !wabaId.trim()}
              style={{
                backgroundColor: '#B2400B',
                borderColor: '#B2400B',
                padding: '10px'
              }}
            >
              <FaUser className="me-2" />
              {signInLoading ? 'Signing in...' : 'Sign In'}
            </Button>

            {/* Erase All Data Button */}
            <Button
              variant="outline-danger"
              className="w-100"
              onClick={handleEraseData}
              disabled={signInLoading}
              style={{
                borderColor: '#dc3545',
                color: '#dc3545'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#dc3545';
              }}
            >
              <FaTrash className="me-2" />
              Erase all data
            </Button>
          </Modal.Body>
        </Modal>
      </>
    );
  }

  switch (whatsAppConnectionStatus) {
    case ServerConnectionStatus.CHECKING:
      return (
        <div className="whatsapp-setup-wrapper">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <p>Checking WhatsApp connection status...</p>
          </div>
        </div>
      );
    case ServerConnectionStatus.NOT_CONNECTED:
    case ServerConnectionStatus.UNKNOWN:
      return (
        <div className="whatsapp-setup-wrapper">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>WhatsApp connection failed. Please try again.</p>
          </div>
        </div>
      );
    case ServerConnectionStatus.NEEDS_CONFIGURATION:
      return (
        <WhatsAppConfiguration
          onSubmit={handleSaveConfiguration}
          loading={loadingStates.savingConfiguration}
          initialValues={{ businessOwnerId: userID }}
          error={configurationError}
        />
      );
    case ServerConnectionStatus.NEEDS_RECONNECT:
    case ServerConnectionStatus.PENDING:
      return (
        <WhatsAppOAuth
          onOAuthClicked={connectWhatsAppForOAuth}
          onGenerateWebhookClicked={generateWebhook}
          whatsAppOAuthConnected={whatsAppOAuthStatus === WhatsAppOAuthStatus.CONNECTED || whatsAppConnectionStatus === ServerConnectionStatus.PENDING}
          webhookGenerationCompleted={webhookGenerated}
          webhookCredentialsValues={webhookCredentials}
          loadingStatesValues={loadingStates}
          onConfirmWebhookUpdatedClicked={confirmWebhookUpdated}
        />
      );
    case ServerConnectionStatus.CONNECTED:
    default:
      return (
        <div className="py-5">
          <Container fluid>
            {/* Header Section with Sign Out Button */}
            <Row className="mb-2 align-items-center">
              <Col>
                <h1 className="display-5 fw-bold mb-0">WhatsApp Business</h1>
              </Col>
              {isLoggedIn && (
                <Col xs="auto">
                  <Button
                    variant="outline-danger"
                    className="d-inline-flex align-items-center justify-content-center flex-nowrap gap-2"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={handleSignOut}
                  >
                    <FaSignOutAlt size={14} className="flex-shrink-0" aria-hidden />
                    <span>Sign out</span>
                  </Button>
                </Col>
              )}
            </Row>

            {/* Navigation Tabs */}
            <Row className="mb-2">
              <Col>
                <NurturixCustomTabs
                  tabs={tabs}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
              </Col>
            </Row>

            {/* Tab Content */}
            <Row>
              <Col>
                {activeTab === WhatsAppTabIds.WHATSAPP && (
                  <WhatsAppChat />
                )}
                {activeTab === WhatsAppTabIds.TEMPLATES && (
                  <WhatsAppTemplate />
                )}
                {activeTab === WhatsAppTabIds.GROUP && (
                  <Group />
                )}
                {activeTab === WhatsAppTabIds.BROADCAST && (
                  <Broadcast />
                )}
              </Col>
            </Row>
          </Container>
        </div>
      );
  }
};

export default WhatsApp;
